/**
 * Cremation fulfillment journey shapes.
 * Mirrors backend/src/modules/cremation-fulfillment/cremation-fulfillment.validators.ts.
 */
import type { CremationServiceType } from '@/features/cremation/types';
import type { CatalogImage } from '@/features/catalog-image/types';

export type PetSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';

export type CremationFulfillmentStatus =
  | 'CONFIRMED'
  | 'PICKED_UP'
  | 'CREMATION_IN_PROGRESS'
  | 'ASHES_READY'
  | 'ASHES_RETURNED';

export type CremationCertificate = {
  certificate_number: string;
  cremated_at: string;
  deceased_pet_name: string | null;
  deceased_pet_species: PetSpecies | null;
  deceased_pet_weight_kg: string | null;
  service_type: CremationServiceType | null;
  provider_name: string | null;
  provider_city: string | null;
  provider_state: string | null;
};

export type CremationFulfillment = {
  booking_id: string;
  status: CremationFulfillmentStatus;
  /** The offering type — BURIAL flips the journey/certificate wording. */
  service_type: CremationServiceType | null;
  provider_name: string | null;
  confirmed_pickup_at: string | null;
  picked_up_at: string | null;
  cremation_started_at: string | null;
  ashes_ready_at: string | null;
  ashes_returned_at: string | null;
  note: string | null;
  /** Provider-attached journey photos (≤5). */
  photos: CatalogImage[];
  /** Present only once the journey reaches ASHES_RETURNED. */
  certificate: CremationCertificate | null;
};

/** A burial offering reframes the whole journey away from cremation/ashes language. */
export function is_burial(service_type?: CremationServiceType | null): boolean {
  return service_type === 'BURIAL';
}

/** Forward-only journey order (mirrors the backend STAGE_ORDER). */
export const CREMATION_STAGE_ORDER: CremationFulfillmentStatus[] = [
  'CONFIRMED',
  'PICKED_UP',
  'CREMATION_IN_PROGRESS',
  'ASHES_READY',
  'ASHES_RETURNED',
];

/** Calm, owner-facing labels for each stage (cremation vs burial). */
const CREMATION_STAGE_LABEL: Record<CremationFulfillmentStatus, string> = {
  CONFIRMED: 'Confirmed',
  PICKED_UP: 'Picked up',
  CREMATION_IN_PROGRESS: 'Cremation in progress',
  ASHES_READY: 'Ashes ready',
  ASHES_RETURNED: 'Ashes returned',
};

const BURIAL_STAGE_LABEL: Record<CremationFulfillmentStatus, string> = {
  CONFIRMED: 'Confirmed',
  PICKED_UP: 'Picked up',
  CREMATION_IN_PROGRESS: 'Burial in progress',
  ASHES_READY: 'Resting place ready',
  ASHES_RETURNED: 'Laid to rest',
};

export function stage_label(
  status: CremationFulfillmentStatus,
  service_type?: CremationServiceType | null,
): string {
  return (is_burial(service_type) ? BURIAL_STAGE_LABEL : CREMATION_STAGE_LABEL)[status];
}

/** The timestamp field each stage stamps, for rendering a dated timeline. */
export const CREMATION_STAGE_TIMESTAMP: Record<CremationFulfillmentStatus, keyof CremationFulfillment> = {
  CONFIRMED: 'confirmed_pickup_at',
  PICKED_UP: 'picked_up_at',
  CREMATION_IN_PROGRESS: 'cremation_started_at',
  ASHES_READY: 'ashes_ready_at',
  ASHES_RETURNED: 'ashes_returned_at',
};

/** The verb a provider taps to move into each stage (CONFIRMED has no advance). */
const CREMATION_ADVANCE_LABEL: Partial<Record<CremationFulfillmentStatus, string>> = {
  PICKED_UP: 'Mark picked up',
  CREMATION_IN_PROGRESS: 'Begin cremation',
  ASHES_READY: 'Mark ashes ready',
  ASHES_RETURNED: 'Mark ashes returned',
};

const BURIAL_ADVANCE_LABEL: Partial<Record<CremationFulfillmentStatus, string>> = {
  PICKED_UP: 'Mark picked up',
  CREMATION_IN_PROGRESS: 'Begin burial',
  ASHES_READY: 'Mark resting place ready',
  ASHES_RETURNED: 'Mark laid to rest',
};

export function advance_label(
  status: CremationFulfillmentStatus,
  service_type?: CremationServiceType | null,
): string {
  return (is_burial(service_type) ? BURIAL_ADVANCE_LABEL : CREMATION_ADVANCE_LABEL)[status] ?? 'Advance';
}

export type AdvanceFulfillmentInput = {
  to: CremationFulfillmentStatus;
  note?: string;
};

export type { CremationServiceType };
